import { Application, Container, Graphics, Sprite, Text, TextStyle, Texture } from 'pixi.js'

export interface Camera {
  x: number
  y: number
  zoom: number
  targetX: number
  targetY: number
  targetZoom: number
}

export class Renderer {
  app: Application
  camera: Camera
  layers: {
    terrain: Container
    buildings: Container
    units: Container
    effects: Container
    ui: Container
    fog: Container
  }
  private worldContainer: Container
  private tileSize: number = 48

  constructor() {
    this.app = new Application()
    this.camera = {
      x: 0,
      y: 0,
      zoom: 1,
      targetX: 0,
      targetY: 0,
      targetZoom: 1
    }
    this.worldContainer = new Container()
    this.layers = {
      terrain: new Container(),
      buildings: new Container(),
      units: new Container(),
      effects: new Container(),
      ui: new Container(),
      fog: new Container()
    }
  }

  async init(container: HTMLElement): Promise<void> {
    await this.app.init({
      background: 0x0a0a0f,
      resizeTo: container,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true
    })

    container.appendChild(this.app.canvas)

    // Set up layer hierarchy
    this.worldContainer.addChild(this.layers.terrain)
    this.worldContainer.addChild(this.layers.buildings)
    this.worldContainer.addChild(this.layers.units)
    this.worldContainer.addChild(this.layers.effects)
    this.worldContainer.addChild(this.layers.fog)
    this.app.stage.addChild(this.worldContainer)
    this.app.stage.addChild(this.layers.ui)

    // Enable sorting for units (y-based depth)
    this.layers.units.sortableChildren = true
    this.layers.buildings.sortableChildren = true
  }

  get width(): number {
    return this.app.screen.width
  }

  get height(): number {
    return this.app.screen.height
  }

  get tileScale(): number {
    return this.tileSize * this.camera.zoom
  }

  setTileSize(size: number): void {
    this.tileSize = size
  }

  // Convert world coordinates to screen coordinates
  worldToScreen(worldX: number, worldY: number): { x: number; y: number } {
    return {
      x: (worldX - this.camera.x) * this.camera.zoom + this.width / 2,
      y: (worldY - this.camera.y) * this.camera.zoom + this.height / 2
    }
  }

  // Convert screen coordinates to world coordinates
  screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
    return {
      x: (screenX - this.width / 2) / this.camera.zoom + this.camera.x,
      y: (screenY - this.height / 2) / this.camera.zoom + this.camera.y
    }
  }

  // Convert world coordinates to tile coordinates
  worldToTile(worldX: number, worldY: number): { x: number; y: number } {
    return {
      x: Math.floor(worldX / this.tileSize),
      y: Math.floor(worldY / this.tileSize)
    }
  }

  // Convert tile coordinates to world coordinates (center of tile)
  tileToWorld(tileX: number, tileY: number): { x: number; y: number } {
    return {
      x: tileX * this.tileSize + this.tileSize / 2,
      y: tileY * this.tileSize + this.tileSize / 2
    }
  }

  // Set camera position (world coordinates)
  setCameraPosition(x: number, y: number, immediate: boolean = false): void {
    this.camera.targetX = x
    this.camera.targetY = y
    if (immediate) {
      this.camera.x = x
      this.camera.y = y
    }
  }

  // Set camera zoom, optionally towards a focal point (world coordinates)
  setCameraZoom(zoom: number, immediate: boolean = false, focalWorldX?: number, focalWorldY?: number): void {
    const oldZoom = this.camera.targetZoom
    const newZoom = Math.max(0.5, Math.min(2, zoom))
    this.camera.targetZoom = newZoom

    // If focal point provided, adjust camera position to keep that point stationary on screen
    if (focalWorldX !== undefined && focalWorldY !== undefined) {
      // The focal point should stay at the same screen position after zoom
      // Calculate how much the view changes at the focal point
      const zoomRatio = newZoom / oldZoom

      // Offset from camera center to focal point
      const offsetX = focalWorldX - this.camera.targetX
      const offsetY = focalWorldY - this.camera.targetY

      // After zoom, we need to move camera so focal point stays in same screen spot
      // New offset should be: oldOffset / zoomRatio (because zoom magnifies the offset)
      // So camera needs to move by: oldOffset - newOffset = oldOffset * (1 - 1/zoomRatio)
      this.camera.targetX += offsetX * (1 - 1 / zoomRatio)
      this.camera.targetY += offsetY * (1 - 1 / zoomRatio)
    }

    if (immediate) {
      this.camera.zoom = this.camera.targetZoom
      if (focalWorldX !== undefined && focalWorldY !== undefined) {
        this.camera.x = this.camera.targetX
        this.camera.y = this.camera.targetY
      }
    }
  }

  // Pan camera by delta
  panCamera(dx: number, dy: number): void {
    this.camera.targetX += dx / this.camera.zoom
    this.camera.targetY += dy / this.camera.zoom
  }

  // Clamp camera to map bounds
  clampCamera(mapWidth: number, mapHeight: number): void {
    const worldWidth = mapWidth * this.tileSize
    const worldHeight = mapHeight * this.tileSize
    const viewWidth = this.width / this.camera.zoom
    const viewHeight = this.height / this.camera.zoom

    this.camera.targetX = Math.max(viewWidth / 2, Math.min(worldWidth - viewWidth / 2, this.camera.targetX))
    this.camera.targetY = Math.max(viewHeight / 2, Math.min(worldHeight - viewHeight / 2, this.camera.targetY))
  }

  // Update camera smoothing
  updateCamera(dt: number): void {
    const smoothing = 1 - Math.pow(0.001, dt)
    this.camera.x += (this.camera.targetX - this.camera.x) * smoothing
    this.camera.y += (this.camera.targetY - this.camera.y) * smoothing
    this.camera.zoom += (this.camera.targetZoom - this.camera.zoom) * smoothing

    // Apply camera transform to world container
    this.worldContainer.position.set(this.width / 2, this.height / 2)
    this.worldContainer.scale.set(this.camera.zoom)
    this.worldContainer.pivot.set(this.camera.x, this.camera.y)
  }

  // Get visible tile bounds for culling
  getVisibleBounds(): { minX: number; minY: number; maxX: number; maxY: number } {
    const margin = 2  // Extra tiles for smooth scrolling
    const topLeft = this.screenToWorld(0, 0)
    const bottomRight = this.screenToWorld(this.width, this.height)

    return {
      minX: Math.floor(topLeft.x / this.tileSize) - margin,
      minY: Math.floor(topLeft.y / this.tileSize) - margin,
      maxX: Math.ceil(bottomRight.x / this.tileSize) + margin,
      maxY: Math.ceil(bottomRight.y / this.tileSize) + margin
    }
  }

  // Clear a layer
  clearLayer(layer: Container): void {
    layer.removeChildren()
  }

  // Create a colored rectangle sprite
  createRect(width: number, height: number, color: number, alpha: number = 1): Graphics {
    const g = new Graphics()
    g.rect(0, 0, width, height)
    g.fill({ color, alpha })
    return g
  }

  // Create a circle sprite
  createCircle(radius: number, color: number, alpha: number = 1): Graphics {
    const g = new Graphics()
    g.circle(0, 0, radius)
    g.fill({ color, alpha })
    return g
  }

  // Create text
  createText(text: string, style?: Partial<TextStyle>): Text {
    const defaultStyle: Partial<TextStyle> = {
      fontFamily: 'Arial',
      fontSize: 14,
      fill: 0xffffff,
      ...style
    }
    return new Text({ text, style: defaultStyle })
  }

  // Add ticker callback
  addTicker(callback: (dt: number) => void): void {
    this.app.ticker.add((ticker) => callback(ticker.deltaMS / 1000))
  }

  // Destroy renderer
  destroy(): void {
    this.app.destroy(true)
  }
}
